import { FC, useEffect, useMemo, useRef, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { AddIcon } from "@chakra-ui/icons";
import { deletePerson, getPeople, newPerson, updatePerson } from "./people-service";
import {
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  VStack,
  Spinner,
  Center,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Skeleton,
  Stack,
  useToast,
  FormControl,
  FormLabel,
  Input as ChakraInput,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
} from '@chakra-ui/react';
import { IProfile, useAuth } from "@bemap/utils";
import { Table, TableRowActions, Panel, PageHeading } from "@bemap/ui";
import { useTranslation } from "react-i18next";
import { formatDate, formatWithCallback } from "@bemap/utils";
import { PhoneInput } from "@bemap/ui";

const PeoplePage: FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const { t } = useTranslation();

  // STATES
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [modalType, setModalType] = useState<"edit" | "new" | "delete">();
  const [people, setPeople] = useState<IProfile[]>([]);
  const [selectedPerson, setSelectedPerson] = useState<IProfile | null>(null);
  const [updateData, setUpdateData] = useState<string>("");
  const [isDeleting, setIsDeleting] = useState(false);

  const initialPerson: IProfile = {
    id: 0,
    name: '',
    email: '',
    phone: '',
    team_id: user?.currentTeamId,
    listProfiles: [],
    assessments: []
  };

  const columns = useMemo<ColumnDef<IProfile, any>[]>(
    () => [
      {
        accessorFn: (row: IProfile) => row.name,
        id: "Nome",
        header: t('analyst.people_table_name'),
        cell: (info: any) => info.getValue(),
        footer: (props: any) => props.column.id,
      },
      {
        accessorFn: (row: IProfile) => row.email,
        id: "Email",
        header: t('analyst.people_table_email'),
        cell: (info: any) => info.getValue(),
        footer: (props: any) => props.column.id,
      },
      {
        accessorFn: (row: IProfile) => row.phone,
        id: "Telefone",
        header: t('analyst.people_table_phone'),
        cell: (info: any) => info.getValue(),
        footer: (props: any) => props.column.id,
      },
      {
        accessorFn: (row: IProfile) => formatDate(row.created_at || ""),
        id: "Criado em",
        header: t('analyst.people_table_created'),
        cell: (info: any) => info.getValue(),
        footer: (props: any) => props.column.id,
      },
      {
        accessorFn: (row: IProfile) => row,
        id: "Ações",
        enableColumnFilter: false,
        enableHiding: true,
        header: t('table.actions'),
        cell: (row: any) => {
          return (
            <TableRowActions
              editAction={() => editPerson(row.getValue())}
              deleteAction={() => openDeleteDialog(row.getValue())}
            />
          );
        },
      },
    ],
    [t]
  );

  useEffect(() => {
    loadPeople();
  }, [updateData]);

  function closeModal() {
    onClose();
    setSelectedPerson(initialPerson);
  }

  async function loadPeople() {
    setIsLoading(true);
    try {
      const { message } = await getPeople();
      setPeople(message);
      setIsLoading(false);
    } catch (error) {
      setPeople([]);
      setIsLoading(false);
    }
  }

  async function editPerson(person: IProfile) {
    try {
      setSelectedPerson(person);
      setModalType("edit");
      onOpen();
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  }

  async function openNewPersonModal() {
    setSelectedPerson(initialPerson);
    setModalType("new");
    onOpen();
  }

  async function openDeleteDialog(person: IProfile) {
    try {
      setSelectedPerson(person);
      setModalType("delete");
      onOpen();
    } catch (error) {
      setIsLoading(false);
    }
  }

  async function createNewPerson() {
    if (!selectedPerson) return;

    setIsLoading(true);
    setIsSaving(true);
    toast({
      title: t('general.saving'),
      status: 'loading',
      isClosable: true,
    });
    try {
      await newPerson(selectedPerson);
      setIsLoading(false);
      setIsSaving(false);
      closeModal();
      setUpdateData(new Date().toLocaleString());
    } catch (error) {
      setIsLoading(false);
      setIsSaving(false);
    } finally {
      toast.closeAll();
    }
  }

  const handleUpdatePerson = async (payload: IProfile) => {
    setIsSaving(true);
    try {
      toast({
        title: t('general.loading'),
        status: 'loading',
        isClosable: true,
      });
      await updatePerson(payload);
      setUpdateData(new Date().toLocaleString());
      closeModal();
    } catch (error: any) {
      if (error.message === 'email_already_exists') {
        toast.closeAll();
        toast({
          title: t('general.email_already_exists'),
          status: 'error',
          isClosable: true,
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePerson = async () => {
    if (!selectedPerson) return;

    try {
      setIsDeleting(true);
      toast({
        title: t('general.deleting'),
        status: 'loading',
        isClosable: true,
      });
      await deletePerson(selectedPerson.id || 0);
      setUpdateData(new Date().toLocaleString());
      closeModal();
    } catch (error) {
      toast({
        title: t('general.error'),
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
      toast.closeAll();
    }
  };

  async function handleChange(event: any) {
    if (!selectedPerson) return;

    setSelectedPerson({
      ...selectedPerson,
      [event.target.name]: event.target.value,
    });
  }

  function RenderModal() {
    switch (modalType) {
      case "edit":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('analyst.people_edit')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={6}>
                  {/* Person Information */}
                  <VStack w="100%" spacing={4} align="stretch">
                    <FormControl isRequired>
                      <FormLabel>{t('analyst.people_new_name')}</FormLabel>
                      <ChakraInput
                        name="name"
                        placeholder={t('general.placeholder_name')}
                        value={selectedPerson?.name || ""}
                        onChange={handleChange}
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>E-mail</FormLabel>
                      <ChakraInput
                        name="email"
                        type="email"
                        placeholder={t('general.placeholder_email')}
                        value={selectedPerson?.email || ""}
                        onChange={handleChange}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t('analyst.people_table_phone')}</FormLabel>
                      <PhoneInput
                        value={selectedPerson?.phone as string || ""}
                        onChange={(phone: string) => setSelectedPerson((prev) => ({ ...prev!, phone }))}
                      />
                    </FormControl>
                  </VStack>

                  {/* Additional Information */}
                  <VStack w="100%" spacing={4} align="stretch">
                    <Accordion allowToggle>
                      <AccordionItem>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            {t('actions.additional_info')}
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel pb={4}>
                          <FormControl id="company">
                            <FormLabel>{t('analyst.company')}</FormLabel>
                            <ChakraInput
                              type="text"
                              defaultValue={selectedPerson?.listProfiles?.length && selectedPerson?.listProfiles[0]?.company?.name || selectedPerson?.assessments && selectedPerson?.assessments[0]?.company?.name || ""}
                              placeholder={t('general.placeholder_company')}
                              onChange={(e) => {
                                setSelectedPerson((prev) => ({
                                  ...prev!,
                                  company: {
                                    name: e.target.value
                                  },
                                }));
                              }}
                            />
                          </FormControl>
                          <FormControl id="birth" mt={4}>
                            <FormLabel>{t('analyst.people_birth_date')}</FormLabel>
                            <ChakraInput
                              type="date"
                              value={formatWithCallback(selectedPerson?.metadata?.birth, 'yyyy-MM-dd', -7)}
                              onChange={(e) => {
                                setSelectedPerson((prev) => ({
                                  ...prev!,
                                  metadata: { ...prev?.metadata, birth: new Date(e.target.value).toISOString() },
                                }));
                              }}
                            />
                          </FormControl>
                        </AccordionPanel>
                      </AccordionItem>
                    </Accordion>
                  </VStack>
                </VStack>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel')}
                </Button>
                <Button
                  colorScheme={"blue"}
                  onClick={() => handleUpdatePerson(selectedPerson!)}
                  isLoading={isSaving}>
                  {t('actions.save')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case "new":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('analyst.people_new')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={6}>
                  {/* Person Information */}
                  <VStack w="100%" spacing={4} align="stretch">
                    <FormControl isRequired>
                      <FormLabel>{t('analyst.people_new_name')}</FormLabel>
                      <ChakraInput
                        name="name"
                        placeholder={t('general.placeholder_name')}
                        value={selectedPerson?.name || ""}
                        onChange={handleChange}
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>E-mail</FormLabel>
                      <ChakraInput
                        name="email"
                        type="email"
                        placeholder={t('general.placeholder_email')}
                        value={selectedPerson?.email || ""}
                        onChange={handleChange}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t('analyst.people_table_phone')}</FormLabel>
                      <PhoneInput
                        value={selectedPerson?.phone as string || ""}
                        onChange={(phone: string) => setSelectedPerson((prev) => ({ ...prev!, phone }))}
                      />
                    </FormControl>
                  </VStack>

                  {/* Additional Information */}
                  <VStack w="100%" spacing={4} align="stretch">
                    <Accordion allowToggle>
                      <AccordionItem>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            {t('actions.additional_info')}
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel pb={4}>
                          <FormControl id="company">
                            <FormLabel>{t('analyst.company')}</FormLabel>
                            <ChakraInput
                              type="text"
                              placeholder={t('general.placeholder_company')}
                              onChange={(e) => {
                                setSelectedPerson((prev) => ({
                                  ...prev!,
                                  company: {
                                    name: e.target.value
                                  },
                                }));
                              }}
                            />
                          </FormControl>
                          <FormControl id="birth" mt={4}>
                            <FormLabel>{t('analyst.people_birth_date')}</FormLabel>
                            <ChakraInput
                              type="date"
                              onChange={(e) => {
                                setSelectedPerson((prev) => ({
                                  ...prev!,
                                  metadata: { ...prev?.metadata, birth: new Date(e.target.value).toISOString() },
                                }));
                              }}
                            />
                          </FormControl>
                        </AccordionPanel>
                      </AccordionItem>
                    </Accordion>
                  </VStack>
                </VStack>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel')}
                </Button>
                <Button colorScheme={"blue"} onClick={createNewPerson} isLoading={isSaving}>
                  {t('actions.save')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case 'delete':
        return (
          <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={closeModal}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                  {t('analyst.people_delete')}
                </AlertDialogHeader>

                <AlertDialogBody>
                  {t('actions.delete_confirm')}
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={closeModal}>
                    {t('actions.cancel')}
                  </Button>
                  <Button colorScheme='red' isLoading={isDeleting} onClick={handleDeletePerson} ml={3}>
                    {t('actions.delete_confirm_button')}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        );
    }
  }

  if (isLoading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <>
      <VStack gap={4} align="stretch" w="100%">
        <PageHeading title={t('partner.people')} actions={[
          {
            label: t('analyst.people_new'),
            icon: <AddIcon />,
            onClick: openNewPersonModal
          }
        ]} />

        <Panel>
          {isLoading ? (
            <Stack mx="20px" my="20px">
              <Skeleton height="20px" isLoaded={!isLoading} />
              <Skeleton height="20px" isLoaded={!isLoading} />
              <Skeleton height="20px" isLoaded={!isLoading} />
            </Stack>
          ) : (
            people && (
              <Table
                tableId="clients-people-table"
                columns={columns}
                data={people}
              />
            )
          )}
        </Panel>
      </VStack>
      {RenderModal()}
    </>
  );
};

export default PeoplePage; 